// TimelineView — top-level composite for the chronological timeline redesign.
//
// Renders the full chronological trip view as a single vertical stack:
//   - A TripOverviewCard first (at-a-glance trip summary), followed by
//   - One PhaseContainer per non-empty journey phase, in the canonical
//     [departure, destination, return] order (Req 1.1, 2.6).
//
// The stack is constrained to a maximum content width of 1280px and centered
// horizontally so the timeline never sprawls on wide viewports (Req 10.4).
//
// A visually-hidden aria-live="polite" region announces the label of the
// current in-progress step so screen-reader users are notified when a step
// transitions into the in-progress state (Req 11.4).
//
// Consumes the `model` object produced by buildTimelineModel:
//   { overview, phases, errors }
// where `phases` is an array of phase view models ({ id, title, description,
// badge, steps }) already grouped/ordered by the pure logic layer. The
// component guards against a null/undefined model and renders nothing in that
// case so it is safe to mount before the model is computed.
//
// Validates: Requirements 1.1, 2.6, 10.4, 11.4

import React from 'react';

import { spacing } from '../../../styles/designTokens';
import { TripOverviewCard } from './TripOverviewCard';
import { PhaseContainer } from './PhaseContainer';

/**
 * Canonical journey-phase order. Phases are rendered as a subsequence of this
 * order regardless of the order they happen to arrive in the model.
 */
const PHASE_ORDER = ['departure', 'destination', 'return'];

/**
 * Visually-hidden style — keeps the aria-live region in the accessibility tree
 * while removing it from the visual layout (the standard sr-only pattern used
 * elsewhere in the journey UI).
 */
const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * Finds the label of the first in-progress step across all phases, scanning in
 * canonical phase order. Returns an empty string when no step is in-progress.
 * `assignStepStatuses` guarantees at most one step is in-progress, so the first
 * match is the (only) in-progress step.
 *
 * @param {Array<{ steps?: Array<{ status?: string, title?: string }> }>} phases
 * @returns {string}
 */
function findInProgressLabel(phases) {
  if (!Array.isArray(phases)) {
    return '';
  }
  for (const phase of phases) {
    const steps = phase && Array.isArray(phase.steps) ? phase.steps : [];
    for (const step of steps) {
      if (step && step.status === 'in-progress') {
        return step.title || '';
      }
    }
  }
  return '';
}

/**
 * @typedef {Object} TimelineModel
 * @property {Object} overview - Summary view model for TripOverviewCard.
 * @property {Array<Object>} phases - Phase view models for PhaseContainer.
 * @property {Array<*>} [errors] - Non-fatal issues collected while building.
 *
 * @typedef {Object} TimelineViewProps
 * @property {TimelineModel|null|undefined} model - The timeline view model.
 * @property {boolean} [isMobile] - Collapses cards/steps to a single column.
 */

/**
 * TimelineView renders the trip overview followed by each non-empty phase.
 *
 * @param {TimelineViewProps} props
 * @returns {React.ReactElement|null}
 */
export function TimelineView({ model, isMobile = false }) {
  // Guard against a null/undefined or non-object model — render nothing so the
  // component is safe to mount before the model is available (Req totality).
  if (!model || typeof model !== 'object') {
    return null;
  }

  const { overview, phases } = model;
  const phaseList = Array.isArray(phases) ? phases : [];

  // Order the phases as a subsequence of [departure, destination, return].
  // groupIntoPhases already returns this order, but we sort defensively so the
  // view is correct regardless of how the model arranged them.
  const orderedPhases = [...phaseList]
    .filter((phase) => phase && typeof phase === 'object')
    .sort((a, b) => {
      const ai = PHASE_ORDER.indexOf(a.id);
      const bi = PHASE_ORDER.indexOf(b.id);
      return (ai === -1 ? PHASE_ORDER.length : ai) - (bi === -1 ? PHASE_ORDER.length : bi);
    });

  const inProgressLabel = findInProgressLabel(orderedPhases);

  return (
    <div
      data-testid="timeline-view"
      style={{
        width: '100%',
        maxWidth: '1280px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.sectionGap,
      }}
    >
      {/* aria-live region announcing the current in-progress step (Req 11.4). */}
      <div
        aria-live="polite"
        aria-atomic="true"
        data-testid="timeline-live-region"
        style={VISUALLY_HIDDEN}
      >
        {inProgressLabel ? `Now: ${inProgressLabel}` : ''}
      </div>

      <TripOverviewCard overview={overview} isMobile={isMobile} />

      {orderedPhases.map((phase) => (
        <PhaseContainer key={phase.id} phase={phase} isMobile={isMobile} />
      ))}
    </div>
  );
}

export default TimelineView;
